// ProjectContext — tracks the operator's "active project" globally.
//
// The asset library, AssetPicker, and upload dialogs all read the active
// project_id from this context and either filter their queries or stamp
// it onto newly-created rows. The selected project is persisted in
// localStorage so the choice survives reloads.
//
// Shape:
//   const { projects, activeProject, setActiveProjectId, refresh } = useActiveProject();
//
//   projects        → array of {id, name, description, owner_id, members[]}
//   activeProject   → the currently-selected project object, or null when
//                     the operator picked "Personal Workspace"
//   setActiveProjectId(id | null) → switch the active project; null means
//                                   personal workspace.
//   refresh()       → re-fetch projects from /api/projects (useful after
//                     creating/joining/deleting a project).
//
// Storage key: localStorage["nostromorama:activeProjectId"]
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";
import { confirmLeave } from "../lib/unsavedWork";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "nostromorama:activeProjectId";

const ProjectContext = createContext({
  projects: [],
  loading: false,
  activeProjectId: null,
  activeProject: null,
  setActiveProjectId: () => {},
  refresh: () => {},
});

export const useActiveProject = () => useContext(ProjectContext);

export function ProjectProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const userId = user?.user_id || null;
  const currentUserId = useRef(userId);
  currentUserId.current = userId;
  const requestVersion = useRef(0);
  const [snapshot, setSnapshot] = useState({ userId: null, projects: [] });
  // Hide the previous account's data during render, before effects can run.
  const projects = useMemo(() => snapshot.userId === userId ? snapshot.projects : [], [snapshot, userId]);
  const [loading, setLoading] = useState(false);
  const [activeProjectId, setActiveProjectIdState] = useState(() => {
    try {
      const v = (typeof window !== "undefined")
        ? window.localStorage.getItem(STORAGE_KEY) : null;
      return v && v.length > 0 ? v : null;
    } catch (_e) {
      return null;
    }
  });

  const refresh = useCallback(async () => {
    const version = ++requestVersion.current;
    if (!userId) {
      setSnapshot({ userId: null, projects: [] });
      setLoading(false);
      return;
    }
    const isCurrent = () => version === requestVersion.current && currentUserId.current === userId;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/projects`);
      if (isCurrent()) setSnapshot({ userId, projects: Array.isArray(r.data) ? r.data : [] });
    } catch (_e) {
      if (isCurrent()) setSnapshot({ userId, projects: [] });
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [userId]);

  // Run a single fetch on mount. We don't refresh on every nav because
  // the project list rarely changes; consumers can call `refresh()`
  // explicitly after create/delete.
  useEffect(() => {
    if (authLoading) return;
    refresh();
    return () => { requestVersion.current += 1; };
  }, [authLoading, refresh]);

  const activeIdRef = useRef(activeProjectId);
  activeIdRef.current = activeProjectId;
  // Devuelve false si el usuario cancela el cambio. Cambiar de proyecto remonta
  // el editor: lo no enviado se guarda como borrador de ESE proyecto, pero los
  // ficheros locales sin subir se pierden, y eso se pregunta antes.
  const setActiveProjectId = useCallback((id) => {
    const clean = (id || "").trim() || null;
    if (clean === activeIdRef.current) return true;
    if (!confirmLeave("proyecto")) return false;
    setActiveProjectIdState(clean);
    try {
      if (clean) window.localStorage.setItem(STORAGE_KEY, clean);
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch (_e) {
      // Quota / private mode — ignore, fall back to in-memory state.
    }
    return true;
  }, []);

  const activeProject = useMemo(
    () => projects.find((p) => p.id === activeProjectId) || null,
    [projects, activeProjectId],
  );

  const value = useMemo(
    () => ({ projects, loading, activeProjectId, activeProject,
             setActiveProjectId, refresh }),
    [projects, loading, activeProjectId, activeProject,
     setActiveProjectId, refresh],
  );

  return (
    <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>
  );
}
